/**
 * Enquête n°27 — Adapté de l'Étude de Cas CCS n°27
 * (Lois physiques spécifiques à la prise de virage.)
 */

import { Lightbulb, ShieldCheck, AlertTriangle, Quote, Users } from 'lucide-react';

export const physiqueViragesEnquete = {
  slug: 'physique-virages-moto',
  themeCode: 'V',
  issueNumber: 27,
  totalIssues: 27,
  series: 'enquetes',
  category: 'Enquête physique du virage',
  shortTitle: 'Physique du virage',
  title: 'Le virage en moto : pourquoi tu te penches (et pourquoi tu peux tomber)',
  kicker: 'Enquête n°27 · Série « Témoignages & Sécurité » · Final !',
  subtitle:
    "40 % des accidents mortels moto = perte de contrôle en virage. Pourtant le virage obéit à des lois précises. Centrifuge, adhérence, point de corde, trajectoire de sécurité. Tu sauras enfin pourquoi tu rates ce virage de la D7.",
  heroImage: '/img/magazines/route.jpg',
  heroImageAlt: 'Moto en virage avec inclinaison — illustration de la physique du virage',
  readTimeMin: 7,
  seoDescription:
    'Enquête sur la physique du virage en moto : force centrifuge, adhérence, point de corde, trajectoire de sécurité, 40 % des accidents mortels. Comment piloter en sécurité selon la physique.',
  seoKeywords:
    'virage moto, force centrifuge moto, inclinaison moto, point de corde, trajectoire de sécurité, physique virage 2 roues, accidents virage',

  edito: {
    title: 'Le virage, juge de paix du motard',
    body: '<strong>40 %</strong> des accidents mortels moto se produisent en virage (ONISR). Pas en ville. Pas sur autoroute. En virage. Pourquoi ? Parce que le virage exige tout : lecture de la route, anticipation, dosage des commandes, regard posé à la sortie, maîtrise de la peur. C’est là que se séparent les motards qui survivront longtemps des autres. Cette enquête ferme notre série de 27 — par ce qui décide vraiment.',
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: 'La force centrifuge — ton adversaire en virage',
      paragraphs: [
        '<strong>Principe</strong> : en virage, ta moto subit une force qui veut la pousser vers l’extérieur. Pour la compenser, tu te penches vers l’intérieur.',
        '<strong>Formule simplifiée</strong> : Force centrifuge = m × v² / r (masse × vitesse au carré / rayon). <strong>Double la vitesse</strong> = QUADRUPLE la force centrifuge nécessaire à compenser.',
        '<strong>Limite physique sur route</strong> : inclinaison max viable ~40-45° (au-delà, les repose-pieds raclent et la moto se redresse violemment). Sur piste, les pros vont à 60-65° grâce à des pneus adhérents et une chaussée parfaite.',
      ],
      keyPoints: [
        'F = m × v² / r',
        'Vitesse × 2 = force × 4',
        'Limite route : 40-45°',
        'Piste : 60-65° (pas route)',
      ],
},
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: 'L’adhérence — ta seule monnaie',
      paragraphs: [
        '<strong>Concept du cercle d’adhérence</strong> : ton pneu dispose d’un budget d’adhérence total. Tu peux le dépenser pour <strong>freiner</strong>, pour <strong>tourner</strong>, ou pour <strong>accélérer</strong>. Mais tu ne peux pas faire les 3 à 100 % en même temps.',
        '<strong>Conséquence</strong> : si tu freines fort en virage (80 % du budget en freinage), il ne te reste que 20 % pour tourner → la moto file droit (« elle élargit »), souvent vers le fossé extérieur.',
        '<strong>Règle d’or</strong> : on freine AVANT le virage, on tourne penché DEDANS, on réaccelère en SORTIE. Jamais les 2 ensemble.',
      ],
      keyPoints: [
        'Cercle d’adhérence = budget',
        'Freiner OU tourner OU accélérer',
        'Pas tout en même temps',
        'Budget mouillé = 50 %',
      ],
},
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 3',
      title: 'La trajectoire de sécurité (oublie le « point de corde »)',
      paragraphs: [
        "<strong>Le mythe du point de corde</strong> : en circuit, le pilote vise le point intérieur du virage pour optimiser sa vitesse. Sur route, c'est SUICIDAIRE : tu te places au centre de la voie inverse, sans visibilité sur les véhicules venant en face.",
        '<strong>La trajectoire de sécurité (REMC C3.6)</strong> : sur route, on entre EXTÉRIEUR (1/3 gauche pour virage à droite), on reste plus longtemps à l’extérieur pour conserver visibilité et marge, on plonge vers le centre du virage SEULEMENT quand on voit la sortie.',
        '<strong>Résultat</strong> : 2 à 3 m de marge en plus en cas de véhicule en face. Souvent la différence entre rentrer chez soi et finir aux urgences.',
      ],
      keyPoints: [
        'Point de corde = piste',
        'Trajectoire de sécurité = route',
        'Extérieur prolongé',
        'Plonger SEULEMENT si visibilité',
      ],
},
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Le regard, le secret physique des pros',
      body: 'En neurosciences, on appelle ça le <strong>réflexe vestibulo-cervical</strong> : ta tête tourne dans la direction de ton regard, ton corps suit, et ta moto avec. <strong>Tu vas où tu regardes</strong> n’est pas une métaphore — c’est de la physiologie pure. Les motards qui regardent le fossé extérieur en virage finissent dans le fossé extérieur, mécaniquement. Les motards qui regardent la sortie passent. Même moto, même vitesse, deux regards différents = deux destins.',
    },
    {
      type: 'lesson',
      icon: AlertTriangle,
      partLabel: 'Chapitre 4',
      title: 'Les 4 erreurs qui tuent en virage',
      paragraphs: [
        '<strong>1. Freiner DANS le virage</strong> = transfert + perte d’adhérence = chute. Règle : freiner avant, jamais pendant.',
        '<strong>2. Accélérer brutalement au point de corde</strong> = la roue arrière dérape, puis réadhe`re brutalement = high-side (éjection). Dosage progressif obligatoire.',
        "<strong>3. Regarder la roue avant ou le bord intérieur</strong> = ferme la trajectoire, sortie de voie. Règle : porter le regard à la sortie du virage.",
        '<strong>4. Trajectoire « à la corde » sans visibilité</strong> = collision frontale potentielle. Règle : trajectoire de sécurité systématique sur route ouverte.',
      ],
      keyPoints: [
        'Freiner avant, jamais dedans',
        'Doser le gaz en sortie',
        'Regard à la sortie',
        'Trajectoire sécurité sur route',
      ],
},
    {
      type: 'tip',
      icon: Users,
      title: 'Fin de notre série 27 enquêtes',
      body: 'Tu viens de terminer la 27e et dernière enquête de notre série. Si une seule t’a fait changer un réflexe en moto, on a réussi notre mission. <strong>Partage ce contenu</strong> dans ton club, ton entourage, tes élèves. Une vie sauvée par mois équivaut à 12 vies par an. C’est notre objectif. Roule sûr.',
    }
  ],
};

export default physiqueViragesEnquete;
